import { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';

import type { EmbedDemoConfig } from '@/lib/api/features';

interface EmbedDemoModalProps {
  /** The demo to preview, or null when closed. */
  demo: EmbedDemoConfig | null;
  onClose: () => void;
}

/* The app is laid out at its real design resolution (1080p — what these signage
 * apps are authored for) and scaled to FIT (contain) so it renders at the right
 * breakpoint instead of being squished into the iframe's own small viewport.
 * Interactive here (unlike the wall preview) so the visitor can click through. */
const FRAME_W = 1920;
const FRAME_H = 1080;

/**
 * The live app on a properly-scaled 16:9 "screen". The iframe renders at
 * 1280×720 and is contain-scaled to the stage so nothing is cropped; the
 * wrapper is sized to the scaled box so corner-rounding stays crisp.
 */
function ScaledLiveFrame({ url, title }: { url: string; title: string }): JSX.Element {
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) setScale(Math.min(w / FRAME_W, h / FRAME_H));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={stageRef}
      className="relative flex h-full w-full items-center justify-center overflow-hidden"
    >
      {!loaded && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-muted-foreground/50">
          <Loader2 className="h-7 w-7 animate-spin" />
        </div>
      )}
      {scale > 0 && (
        <div
          className="overflow-hidden rounded-lg bg-black"
          style={{ width: FRAME_W * scale, height: FRAME_H * scale }}
        >
          <iframe
            src={url}
            title={title}
            onLoad={() => setLoaded(true)}
            className="origin-top-left border-0 bg-black"
            style={{
              width: `${FRAME_W}px`,
              height: `${FRAME_H}px`,
              transform: `scale(${scale})`,
            }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            loading="lazy"
          />
        </div>
      )}
    </div>
  );
}

/**
 * Live preview of a deployed OptiDev demo app. Dark, full-bleed stage that
 * renders the real signage on a properly-scaled 16:9 screen (the app is built
 * for large displays, so we scale a 1280×720 render to fit rather than cramming
 * it into a small iframe viewport). Footer offers an "open full screen" link and
 * a "Build my own" CTA that seeds the prompt box back on the onboarding page.
 */
export function EmbedDemoModal({ demo, onClose }: EmbedDemoModalProps): JSX.Element | null {
  // Esc closes.
  useEffect(() => {
    if (!demo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [demo, onClose]);

  // Only renders for demos that have a live app to preview. Demos without a
  // deployUrl are handled inline on the page (they seed the prompt instead).
  if (!demo || !demo.deployUrl) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
      style={{ animation: 'meetBackdropIn 200ms ease-out both' }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={demo.label}
        className="relative flex h-[92vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-border"
        style={{ animation: 'meetModalIn 280ms cubic-bezier(0.2, 0, 0, 1) both' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Slim top bar — title + close (X). No footer, so the live app gets
            nearly the full height. "Use this idea" lives on the example card. */}
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 sm:px-5">
          <h3 className="min-w-0 truncate text-sm font-semibold tracking-tight text-foreground sm:text-base">
            {demo.label}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Live preview stage — the app scaled to fit, on a neutral letterbox
            backdrop with even padding on all sides. No drop shadow, ring, or
            top-biased gradient: those made the screen read as a hard-edged card
            floating top-heavy (shadow pooled in the bottom gap, gradient lit the
            top). Padding lives here (not on the measured ScaledLiveFrame) so the
            scale calc sees the true available space and the screen never clips. */}
        <div className="relative flex-1 bg-zinc-100 p-3 dark:bg-zinc-950 sm:p-5">
          <ScaledLiveFrame url={demo.deployUrl} title={demo.label} />
        </div>
      </div>

      {/* Self-contained keyframes so the modal animates even when the Meet
          modal (which also defines these names) isn't mounted. */}
      <style>{`
        @keyframes meetBackdropIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes meetModalIn {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
